import React from "react";
import { Bank, PencilSimple, Trash, WarningCircle } from "@phosphor-icons/react";

import { Empty, LoadingBlock } from "../../../design-system/components/desktop";
import {
  PanelBadge,
  PanelRow,
  PanelRowAction,
  PanelRows,
} from "../../../design-system/patterns/settings";
import { errorText, LOAD_FAILURE } from "../../../utils/errorText";

/**
 * The saved payout accounts. Pure.
 *
 * PRIVACY: an account number is NEVER rendered in full. `maskAccount` keeps the
 * last four digits and nothing else, which is the only form of it that appears
 * anywhere outside the form field the user typed it into. Do not "improve" this
 * into a full number, and do not put it in a title attribute either.
 *
 * The IBN number used to be printed in the detail line in full. It is a bank
 * identifier tied to the account, so it is masked the same way.
 */

const maskAccount = (accountNumber) => {
  if (!accountNumber) return "account on file";
  const str = accountNumber.toString();
  if (str.length <= 4) return `ending in ${str}`;
  return `ending in ${str.slice(-4)}`;
};

const getBankAccountId = (account, index) =>
  account.id || account.bank_account_id || account._id || `account-${index}`;

const PaymentMethodsList = ({
  bankDetails,
  isLoading,
  error,
  onEdit,
  onDelete,
  deleting,
}) => {
  if (isLoading) return <LoadingBlock>Loading your payment methods</LoadingBlock>;

  if (error) {
    return (
      <Empty
        icon={WarningCircle}
        illustration="request-failed"
        tone="error"
        title="We could not load your payment methods"
        text={errorText(error, LOAD_FAILURE)}
        inline
      />
    );
  }

  const bankDetailsArray = Array.isArray(bankDetails) ? bankDetails : [];

  if (bankDetailsArray.length === 0) {
    return (
      <Empty
        icon={Bank}
        illustration="no-payment-methods-yet"
        title="No payment methods yet"
        text="Add a bank account so payouts have somewhere to land."
        inline
      />
    );
  }

  return (
    <PanelRows>
      {bankDetailsArray.map((account, index) => {
        const accountId = getBankAccountId(account, index);
        const bank = account.bank_name || "Bank account";
        const branch = account.branch_name || account.branch_code;

        return (
          <PanelRow
            key={accountId}
            icon={Bank}
            title={`${bank} ${maskAccount(account.account_number)}`}
            badge={account.is_default ? <PanelBadge>Default</PanelBadge> : null}
            detail={[
              account.account_holder_name,
              branch ? `Branch ${branch}` : null,
            ]
              .filter(Boolean)
              .join(" · ")}
            actions={
              <>
                <PanelRowAction
                  icon={PencilSimple}
                  label={`Edit ${bank} account`}
                  disabled={deleting === accountId}
                  onClick={() => onEdit(account)}
                />
                <PanelRowAction
                  icon={Trash}
                  label={`Delete ${bank} account`}
                  disabled={deleting === accountId}
                  onClick={() => onDelete(accountId)}
                />
              </>
            }
          />
        );
      })}
    </PanelRows>
  );
};

export default PaymentMethodsList;
